import { useNavigate, useSearchParams } from 'react-router-dom';
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import MainFrameLeftSection from '../../components/MainFrameLeftSection/mainFrameLeftSection';
// eslint-disable-next-line import/no-cycle
import MainFrameRightSection, {
  DocumentsAndSearchType, Pagination, SearchType, defaultDocumentsAndSearchType, defaultPagination,
} from '../../components/MainFrameRightSection/mainFrameRightSection';
import MainFrame from '../../components/MainFrame/mainFrame';
import styles from './mainPage.module.css';
import usePageTitle from '../../hooks/title.hook';
import folderApi, { Folder } from '../../services/http/folder.api';
import FolderPermissionsSection from '../../components/FolderPermissions/folderPermissionsSection';
import useEffectOnce from '../../hooks/useEffectOnce.hook';
import { useCreateLogApi } from '../../hooks/api/log.hook';
import { LogControlName, LogFormName } from '../../utils/log.utils';
import {
  useDocumentSearchPdfDetail,
  useDocumentSearchDetail,
  useDocumentSearchFolder,
  useGetSearchResultFile,
} from '../../hooks/api/document.hook';
import { useMessageModal } from '../../hooks/modal.hook';
import { ApiError } from '../../services/http';
import {
  DocumentDetail, DocumentOcrPagination, DocumentPagination, DocumentSearchDetailForm, SearchUrlForm, SortList,
} from '../../services/http/documents.api';
import sharedLinkApi from '../../services/http/sharedLink.api';
import LoadingOverlay from '../../components/LoadingOverlay';
import TopBar from '../../components/TopBar/topBar';
import SearchInputTop, {
  ContractType,
  FilterType, mapContractTypeToFlags,
  mapWarehouseStatusToCode,
  SearchInputTopRef,
  WarehouseStatusType,
} from '../../components/SearchInputTop';
import { CloseAlertModal, closeModalInitialState } from '../../components/AlertModal';
import TopBarTagBulkButton from '../../components/TopBar/topBarTagBulkButton';
import useColumnResize from '../../hooks/useColumnResize';
import { LastSearch } from '../../components/SearchInputTop/SearchInputTopFilterModal';
import { useAuthContext } from '../../store/auth.store';
import { WarehouseStatusCode } from '../../utils/warehouse.utils';
import SharedLinkUtils, { SharedLinkPath } from '../../utils/sharedLink.utils';
import SearchConditionUtils from '../../utils/searchCondition.utils';
import PageTitle from '../../utils/pageTitles';

/**
 * 文書一覧メインページ
 */
function Component() {
  usePageTitle(PageTitle.MainPage);

  const searchInputTopRef = useRef<SearchInputTopRef>(null);

  const { user } = useAuthContext();
  // states
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [selectedFolderBackup, setSelectedFolderBackup] = useState<Folder | null>(null);
  const [changingFolderPermissions, setChangingFolderPermissions] = useState(false);
  const [updateFolderList, setUpdateFolderList] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [sort, setSort] = useState<SortList[] | undefined>(undefined);
  const [pagination, setPagination] = useState<Pagination>(defaultPagination);
  const [documentsAndSearchType, setDocumentsAndSearchType] = useState<DocumentsAndSearchType>(defaultDocumentsAndSearchType);
  const [advancedSearchForm, setAdvancedSearchForm] = useState<DocumentSearchDetailForm | null>(null);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [highlightTexts, setHighlightTexts] = useState<string[]>([]);
  const [contractType, setContractType] = useState<ContractType>(ContractType.ALL);
  const [warehouseStatus, setWarehouseStatus] = useState<WarehouseStatusType>(WarehouseStatusType.ALL);
  const [closeAlertModal, setCloseAlertModal] = useState(closeModalInitialState);
  const [isFolderListLoading, setIsFolderListLoading] = useState(false);
  const [isFolderListEmpty, setIsFolderListEmpty] = useState(false);
  const [dropCompletedCounter, setDropCompletedCounter] = useState(0);
  const [lastAdvancedSearchForm, setLastAdvancedSearchForm] = useState<LastSearch | null>(null);
  const [warehouseStatusAdvanced, setWarehouseStatusAdvanced] = useState<WarehouseStatusCode | undefined>();
  const [isLoadingSearchResultFile, setIsLoadingSearchResultFile] = useState<boolean>(false);
  const [isLoadingSharedLink, setIsLoadingSharedLink] = useState<boolean>(false);
  const [simpleSearchForm, setSimpleSearchForm] = useState<DocumentSearchDetailForm | null>(null);
  const [lastSearchForm, setLastSearchForm] = useState<DocumentSearchDetailForm | null>(null);
  const [sharedToken, setSharedToken] = useState<string | null>(null);
  const [searchRangeFolderName, setSearchRangeFolderName] = useState<string | undefined>(undefined);

  const { request: createLog } = useCreateLogApi();
  const { request: documentSearchFolderRequest, loading: loadingFolder } = useDocumentSearchFolder();
  const { request: documentSearchDetailRequest, loading: loadingAdvancedSearch } = useDocumentSearchDetail();
  const { request: documentSearchPdfDetailRequest, loading: loadingDocumentSearchPdfDetail } = useDocumentSearchPdfDetail();
  const { request: getSearchResultFileRequest } = useGetSearchResultFile();

  const openMessageModal = useMessageModal();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { width: leftWidth, handleResizeStart } = useColumnResize('mainFrameLeftSectionWidth', 220);
  const isLoading = loadingFolder || loadingAdvancedSearch || loadingDocumentSearchPdfDetail || isLoadingSharedLink;
  const [isOcrSearchEnabled, setIsOcrSearchEnabled] = useState<boolean>(() => {
    try {
      const userHasOcr = user?.hasOcrSupport ?? false;
      if (!userHasOcr) return false;

      return JSON.parse(localStorage.getItem('is_ocr_search_enabled') ?? 'false');
    } catch {
      return false;
    }
  });
  const [isSearchingOcr, setIsSearchingOcr] = useState(false);

  const onClearSearch = useCallback(() => {
    setSearchText('');
    setIsSearching(false);
    setIsSearchingOcr(false);
    setLastAdvancedSearchForm(null);
  }, [setIsSearchingOcr]);

  const onClearAdvancedSearch = useCallback(() => {
    setAdvancedSearchForm(null);
  }, []);

  const searchFolderDocument = useCallback(async (_folder: Folder, page: number, sortList = sort) => {
    setHighlightTexts([]);
    try {
      const res = await documentSearchFolderRequest({ folder: _folder, page, sortList: sortList || [] });
      setDocumentsAndSearchType({ documents: res.documents, searchType: SearchType.FOLDER });
      setSharedToken(res.sharedToken);
      const totalPages = Math.ceil(res.total / res.pageLimit) || 1;
      setPagination({
        page: res.page,
        totalItems: res.total,
        pageLimit: res.pageLimit,
        totalPages,
      });
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
    }
  }, [documentSearchFolderRequest, openMessageModal, sort]);

  const applyFiltersFromForm = useCallback((updatedForm: DocumentSearchDetailForm) => {
    const wareHouseStatusFromForm = updatedForm.warehouseStatus;
    const contractTypeFromForm = updatedForm.electronicContract;

    if (wareHouseStatusFromForm === WarehouseStatusCode.IN_STORAGE) {
      setWarehouseStatus(WarehouseStatusType.STORED);
    } else if (wareHouseStatusFromForm === WarehouseStatusCode.NOT_RECEIVED) {
      setWarehouseStatus(WarehouseStatusType.NOT_STORED);
    } else {
      setWarehouseStatus(WarehouseStatusType.ALL);
    }

    if (contractTypeFromForm === 1) {
      setContractType(ContractType.ELECTRONIC);
    } else if (contractTypeFromForm === 0) {
      setContractType(ContractType.PAPER);
    } else {
      setContractType(ContractType.ALL);
    }
  }, []);

  const handleAdvancedSearchResponse = useCallback((res: DocumentPagination | DocumentOcrPagination) => {
    setDocumentsAndSearchType({ documents: res.documents, searchType: SearchType.ADVANCED, searchKeyword: res.searchKeyword });
    setSharedToken(res.sharedToken);

    setIsSearching(true);
    setSelectedFolder(null);

    if (isOcrSearchEnabled) {
      setIsSearchingOcr(true);
    } else {
      setIsSearchingOcr(false);
    }

    const totalPages = Math.ceil(res.total / res.pageLimit) || 1;
    setPagination({
      page: res.page,
      totalItems: res.total,
      pageLimit: res.pageLimit,
      totalPages,
    });

    const documentPagination = res as DocumentPagination;
    const documentOcrPagination = res as DocumentOcrPagination;
    if (documentPagination.highlightWordList) {
      setHighlightTexts(documentPagination.highlightWordList);
    } else if (documentOcrPagination.ocrHighlightWordList) {
      setHighlightTexts([]);
    }
  }, [isOcrSearchEnabled]);

  const executeAdvancedSearch = useCallback(async (updatedForm: DocumentSearchDetailForm, page: number, sortList: SortList[] | undefined) => {
    try {
      const res = isOcrSearchEnabled
        ? await documentSearchPdfDetailRequest({ ...updatedForm, page, sortList: sortList || [] })
        : await documentSearchDetailRequest({ ...updatedForm, page, sortList: sortList || [] });

      setLastSearchForm(updatedForm);
      handleAdvancedSearchResponse(res);
    } catch (e) {
      openMessageModal((e as ApiError)?.message);
    }
  }, [isOcrSearchEnabled, documentSearchPdfDetailRequest, documentSearchDetailRequest, handleAdvancedSearchResponse, openMessageModal]);

  const buildSearchForm = useCallback((
    form: DocumentSearchDetailForm,
    warehouseStatusParam: WarehouseStatusType | undefined,
    contractTypeParam: ContractType | undefined,
  ) => {
    const updatedForm: DocumentSearchDetailForm = { ...form };

    if (warehouseStatusParam) updatedForm.warehouseStatus = mapWarehouseStatusToCode(warehouseStatusParam);
    if (contractTypeParam) updatedForm.electronicContract = mapContractTypeToFlags(contractTypeParam);
    return updatedForm;
  }, []);

  const handleAdvancedSearchDocument = useCallback(async (
    form: DocumentSearchDetailForm,
    page: number,
    sortList = sort,
    warehouseStatusParam?: WarehouseStatusType,
    contractTypeParam?: ContractType,
  ) => {
    setSort(sortList);

    const updatedForm = buildSearchForm(form, warehouseStatusParam, contractTypeParam);

    applyFiltersFromForm(updatedForm);

    await executeAdvancedSearch(updatedForm, page, sortList);
  }, [sort, buildSearchForm, applyFiltersFromForm, executeAdvancedSearch]);

  const prepareSimpleSearchParams = useCallback((
    warehouseStatusParam: WarehouseStatusType | undefined,
    contractTypeParam: ContractType | undefined,
  ) => {
    const status = mapWarehouseStatusToCode(warehouseStatusParam || warehouseStatus);
    const electronicContract = mapContractTypeToFlags(contractTypeParam || contractType);
    return { status, electronicContract };
  }, [contractType, warehouseStatus]);

  const handleSimpleSearchReturn = useCallback((res: DocumentPagination | DocumentOcrPagination) => {
    const searchKey = isOcrSearchEnabled ? 'pdfSimpleString' : 'simpleString';
    setLastSearchForm({ [searchKey]: searchText });
    setDocumentsAndSearchType({ documents: res.documents, searchType: SearchType.SIMPLE, searchKeyword: res.searchKeyword });
    setSharedToken(res.sharedToken);

    setIsSearching(true);
    setSelectedFolder(null);

    if (isOcrSearchEnabled) {
      setIsSearchingOcr(true);
    } else {
      setIsSearchingOcr(false);
    }

    const totalPages = Math.ceil(res.total / res.pageLimit) || 1;
    setPagination({
      page: res.page,
      totalItems: res.total,
      pageLimit: res.pageLimit,
      totalPages,
    });
    const documentPagination = res as DocumentPagination;
    const documentOcrPagination = res as DocumentOcrPagination;
    if (documentPagination.highlightWordList) {
      setHighlightTexts(documentPagination.highlightWordList);
    } else if (documentOcrPagination.ocrHighlightWordList) {
      setHighlightTexts([]);
    }
  }, [isOcrSearchEnabled, searchText]);

  const executeSimpleSearch = useCallback(async (
    page: number,
    sortList: SortList[] | undefined,
    text: string,
    status: WarehouseStatusCode | undefined,
    electronicContract: 1 | 0 | undefined,
  ) => {
    try {
      const res = isOcrSearchEnabled
        ? await documentSearchPdfDetailRequest({
          page, sortList: sortList || [], pdfSimpleString: text, warehouseStatus: status, electronicContract,
        })
        : await documentSearchDetailRequest({
          page, sortList: sortList || [], simpleString: text, warehouseStatus: status, electronicContract,
        });
      handleSimpleSearchReturn(res);
    } catch (e) {
      setIsSearching(false);
      setIsSearchingOcr(false);
      openMessageModal((e as ApiError)?.message);
    }
  }, [isOcrSearchEnabled, documentSearchPdfDetailRequest, documentSearchDetailRequest, handleSimpleSearchReturn, openMessageModal]);

  const handleSimpleSearchDocument = useCallback(async (
    text: string,
    page: number,
    sortList = sort,
    warehouseStatusParam?: WarehouseStatusType,
    contractTypeParam?: ContractType,
  ) => {
    setLastAdvancedSearchForm(null);
    setAdvancedSearchForm(null);
    setSort(sortList);

    const { status, electronicContract } = prepareSimpleSearchParams(warehouseStatusParam, contractTypeParam);

    setSimpleSearchForm({
      simpleString: text,
      warehouseStatus: status,
      electronicContract,
    });

    await executeSimpleSearch(page, sortList, text, status, electronicContract);
  }, [sort, prepareSimpleSearchParams, executeSimpleSearch]);

  const handleSearchDocument = useCallback((filterParam: WarehouseStatusType | ContractType, filterType: FilterType) => {
    const isContractFilter = filterType === FilterType.CONTRACT_TYPE;
    const isWarehouseFilter = filterType === FilterType.WAREHOUSE_STATUS_TYPE;

    if (!isContractFilter && !isWarehouseFilter) return;

    const contractTypeParam = isContractFilter ? (filterParam as ContractType) : contractType;
    const warehouseStatusParam = isWarehouseFilter ? (filterParam as WarehouseStatusType) : warehouseStatus;

    if (advancedSearchForm) {
      setSearchText(null);
      handleAdvancedSearchDocument(advancedSearchForm, 1, undefined, warehouseStatusParam, contractTypeParam);
    } else {
      const searchString = simpleSearchForm?.simpleString || '';
      setSearchText(searchString);
      handleSimpleSearchDocument(searchString, 1, undefined, warehouseStatusParam, contractTypeParam);
    }
  }, [advancedSearchForm, contractType, handleAdvancedSearchDocument, handleSimpleSearchDocument, simpleSearchForm, warehouseStatus]);

  const handleFolderSelect = useCallback((folder: Folder) => {
    if (selectedFolder && (selectedFolder.id === folder.id)) return;
    setSelectedFolder(folder);
    setSelectedFolderBackup(folder);
    searchFolderDocument(folder, 1);
    setIsSearching(false);
    setIsSearchingOcr(false);

    setSearchText(null);
    setLastAdvancedSearchForm(null);
    setContractType(ContractType.ALL);
    setWarehouseStatus(WarehouseStatusType.ALL);
  }, [searchFolderDocument, selectedFolder]);

  const handlePageChange = useCallback((page: number) => {
    if (documentsAndSearchType.searchType === SearchType.ADVANCED && advancedSearchForm) {
      handleAdvancedSearchDocument(advancedSearchForm, page);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.SIMPLE && searchText !== null) {
      handleSimpleSearchDocument(searchText, page);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.FOLDER && selectedFolder) {
      searchFolderDocument(selectedFolder, page);
    }
  }, [handleAdvancedSearchDocument, advancedSearchForm, documentsAndSearchType.searchType, handleSimpleSearchDocument, searchFolderDocument, searchText, selectedFolder]);

  const handleAdvancedSearch = useCallback((form: DocumentSearchDetailForm) => {
    setSearchText(null);
    handleAdvancedSearchDocument(form, 1);
    setAdvancedSearchForm(form);
  }, [handleAdvancedSearchDocument]);

  const handleSearch = useCallback((text: string) => {
    handleSimpleSearchDocument(text, 1);
    setSearchText(text);
  }, [handleSimpleSearchDocument]);

  const handleCloseSearchSection = useCallback(() => {
    if (selectedFolder) return;
    setIsSearching(false);
    setIsSearchingOcr(false);

    setDocumentsAndSearchType(defaultDocumentsAndSearchType);
    setSharedToken(null);
    setSearchText(null);
    setLastAdvancedSearchForm(null);
    setAdvancedSearchForm(null);
    setContractType(ContractType.ALL);
    setWarehouseStatus(WarehouseStatusType.ALL);
    if (!selectedFolderBackup) return;
    handleFolderSelect(selectedFolderBackup);
  }, [handleFolderSelect, selectedFolder, selectedFolderBackup]);

  const onRefresh = useCallback(async (page?: number, sortList?: SortList[]) => {
    if (documentsAndSearchType.searchType === SearchType.ADVANCED && advancedSearchForm) {
      await handleAdvancedSearchDocument(advancedSearchForm, page || 1, sortList);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.SIMPLE && searchText !== null) {
      await handleSimpleSearchDocument(searchText, page || 1, sortList);
      return;
    }
    if (documentsAndSearchType.searchType === SearchType.FOLDER && selectedFolder) {
      await searchFolderDocument(selectedFolder, page || 1, sortList);
    }
  }, [handleAdvancedSearchDocument, advancedSearchForm, documentsAndSearchType.searchType, handleSimpleSearchDocument, searchFolderDocument, searchText, selectedFolder]);

  const onSortChanged = useCallback((sortList: SortList[]) => {
    const result = sortList.map(({ item, order }) => ({
      item: item === 'name' ? 'documentName' : item,
      order,
    }));

    setSort(result);
    if (pagination.totalPages !== 1) {
      onRefresh(1, result);
    }
  }, [onRefresh, pagination.totalPages]);

  const onRemoveDocument = useCallback((e: DocumentDetail) => {
    setDocumentsAndSearchType((prev) => ({
      ...prev,
      documents: prev.documents.filter((document) => document.id !== e.id),
    }));
  }, []);

  const handleInvalidSearchAlertModal = useCallback(() => {
    const text = '検索条件が無効です。';
    setCloseAlertModal((prev) => ({
      ...prev,
      open: true,
      text,
      onCancel: () => {
        setCloseAlertModal({ ...prev, open: false });
        navigate('/DCD16001C');
      },
    }));
  }, [navigate]);

  const removeSharedKeyFromUrl = () => {
    const newParams = new URLSearchParams(searchParams);

    newParams.delete('sharedKey');

    setSearchParams(newParams, { replace: true });
  };

  const handleSharedLink = useCallback(async (sharedKey: string) => {
    setIsLoadingSharedLink(true);
    try {
      const res = await sharedLinkApi.resolve({ sharedKey });

      if (res.path === SharedLinkPath.DOCUMENTS_SEARCH_FOLDER || res.path === SharedLinkPath.FOLDER_SEARCH) {
        const params = res.params as Record<string, unknown>;
        const folderId = params.folderId as number;
        const folderResponse = await folderApi.search({});
        const folder = folderResponse.folderList.find((f) => f.id === folderId);
        if (folder) {
          handleFolderSelect(folder);
        } else {
          throw new Error('このリンクは無効です。\nURLが間違っているか、アクセス権がありません。');
        }
      } else if (res.path === SharedLinkPath.DOCUMENTS_SEARCH_DETAIL || res.path === SharedLinkPath.DOCUMENTS_SEARCH_PDF_DETAIL || res.path === SharedLinkPath.REMINDER_SYSTEM_TAG_LIMIT_EXPIRED) {
        const isOcrSearch = res.path === SharedLinkPath.DOCUMENTS_SEARCH_PDF_DETAIL;
        if (isOcrSearch) {
          setIsOcrSearchEnabled(true);
        }
        const data = await SharedLinkUtils.resolveSharedLinkData(res.params as Record<string, unknown>);

        if (data.form.searchRange) {
          const folderResponse = await folderApi.search({});
          const folder = folderResponse.folderList.find((f) => f.id === data.form.searchRange);
          setSearchRangeFolderName(folder?.name);
        } else {
          setSearchRangeFolderName(undefined);
        }

        handleAdvancedSearch(data.form);
        setLastAdvancedSearchForm({
          form: {
            ...data.form,
            simpleString: isOcrSearch ? data.form.pdfSimpleString : data.form.simpleString,
          },
          classification: data.classification,
          tagFilters: data.tagFilters,
          enableFolderSearch: data.enableFolderSearch,
          selectedClassification: data.selectedClassification,
        });
        setWarehouseStatusAdvanced(data.warehouseStatus);

        const conditions = SearchConditionUtils.buildConditions(data.form, {
          documentTypeName: data.classification?.type.documentTypeName,
          folderName: data.form.searchRange ? 'true' : undefined,
        });
        const text = isOcrSearch ? data.form.pdfSimpleString : data.form.simpleString;
        if (conditions.length === 1 && text) {
          setSearchText(text);
        }
      }
      removeSharedKeyFromUrl();
    } catch (e: any) {
      const text = e?.message || '共有リンクが無効です。';
      setCloseAlertModal((prev) => ({
        ...prev,
        open: true,
        text,
        onCancel: () => {
          setCloseAlertModal({ ...prev, open: false });
          navigate('/DCD16001C');
        },
      }));
    } finally {
      setIsLoadingSharedLink(false);
    }
  }, [navigate, handleAdvancedSearch, handleFolderSelect]);

  const getSearchDetailForm = useCallback((name: string | null, memo: string | null, label: string, value1: string) => {
    const form: SearchUrlForm = {};

    form.name = name || form.name;
    form.memo = memo || form.memo;

    const isDateFormat = /^\d{4}[-/]\d{2}[-/]\d{2}$/.test(value1);

    if (!isDateFormat) {
      return null;
    }

    const normalizedDate = value1.replace(/\//g, '-');
    const dateValue = `${normalizedDate}T00:00:00Z`;
    form.tagList = [[{
      label,
      condition: '次の期間',
      value1: dateValue,
      value2: dateValue,
    }]];

    return form;
  }, []);

  const handleDropCompleted = () => {
    setDropCompletedCounter((prev) => prev + 1);
  };

  const handleExportSearchResultDocuments = useCallback(() => {
    const requestForm = selectedFolder
      ? { searchRange: selectedFolder.id }
      : (lastSearchForm || {});

    getSearchResultFileRequest(requestForm);

    // This was requested by the client because this request could take a long time sending confirmation email to the users
    setIsLoadingSearchResultFile(true);
    setTimeout(() => {
      setIsLoadingSearchResultFile(false);
      openMessageModal('リクエストを受け付けました。\n準備ができ次第、メールでお知らせします。');
    }, 1000);
  }, [getSearchResultFileRequest, openMessageModal, selectedFolder, lastSearchForm]);

  useEffectOnce(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sharedKey = urlParams.get('sharedKey');

    if (sharedKey) {
      handleSharedLink(sharedKey);
      return;
    }

    const documentId = urlParams.get('documentId');
    if (documentId) {
      const form = { ccControlNumber: documentId };
      handleAdvancedSearch(form);
      setLastAdvancedSearchForm({
        form,
        classification: undefined,
        tagFilters: [],
        enableFolderSearch: false,
        selectedClassification: null,
      });
      return;
    }

    const name = urlParams.get('documentName');
    const memo = urlParams.get('memo');
    const label = urlParams.get('tag');
    const value1 = urlParams.get('date');

    if (name || memo || label || value1) {
      if (!label || !value1) {
        handleInvalidSearchAlertModal();
        return;
      }
    } else {
      return;
    }

    const searchDetailForm = getSearchDetailForm(name, memo, label, value1);

    if (!searchDetailForm) {
      handleInvalidSearchAlertModal();
      return;
    }

    if (Object.keys(searchDetailForm).length > 0) {
      handleAdvancedSearch(searchDetailForm);
    }
  });

  const handleClickShowAdvancedFilterMenu = () => {
    searchInputTopRef?.current?.setShowAdvancedFilterMenu(true);
  };

  useEffect(() => {
    if (selectedFolder && selectedFolder.id) {
      setChangingFolderPermissions(false);
    }
  }, [selectedFolder]);

  useEffectOnce(() => {
    createLog(LogFormName.Main, LogControlName.Show);
  });

  return (
    <div>
      <MainFrame
        borderBox
        topBarDisplay={false}
        onClearSearch={onClearSearch}
        onClearAdvancedSearch={onClearAdvancedSearch}
      >
        <div className={styles.mainFrame}>
          <TopBar>
            <>
              <SearchInputTop
                ref={searchInputTopRef}
                text={searchText || ''}
                isSearching={isSearching}
                contractType={contractType}
                warehouseStatus={warehouseStatus}
                onAdvancedSearch={handleAdvancedSearch}
                onSearch={handleSearch}
                onTextChange={setSearchText}
                onChangeContractType={(status, filterType) => {
                  setContractType(status);
                  handleSearchDocument(status, filterType);
                }}
                onChangeWarehouseStatus={(status, filterType) => {
                  setWarehouseStatus(status);
                  handleSearchDocument(status, filterType);
                }}
                setIsOcrSearchEnabled={setIsOcrSearchEnabled}
                isOcrSearchEnabled={isOcrSearchEnabled}
                setLastAdvancedSearchForm={setLastAdvancedSearchForm}
                lastAdvancedSearchForm={lastAdvancedSearchForm}
                showOcrSwitch
                onChangeOcrStatus={handleCloseSearchSection}
                warehouseStatusAdvanced={warehouseStatusAdvanced}
                setWarehouseStatusAdvanced={setWarehouseStatusAdvanced}
              />
              <TopBarTagBulkButton />
            </>
          </TopBar>
          <div className={styles.mainSections}>
            <MainFrameLeftSection
              onDocumentFolderChanged={() => onRefresh(pagination.page, sort)}
              folder={selectedFolder}
              onSelectFolder={handleFolderSelect}
              isChangingFolderPermissions={changingFolderPermissions}
              updateFolderList={updateFolderList}
              onFolderListSearchLoading={setIsFolderListLoading}
              onFolderListSearchEmpty={setIsFolderListEmpty}
              onDropCompleted={handleDropCompleted}
              mainFrameLeftSectionWidth={leftWidth}
              onUpdateFolder={(folder) => {
                setSelectedFolder(folder);
              }}
            />

            <div
              role="separator"
              style={{
                width: '5px',
                cursor: 'col-resize',
                backgroundColor: 'transparent',
                margin: '-3px',
                zIndex: 999,
                height: '100%',
              }}
              onMouseDown={handleResizeStart}
            />
            {!changingFolderPermissions && (
            <MainFrameRightSection
              folder={selectedFolder}
              documentsAndSearchType={documentsAndSearchType}
              pagination={pagination}
              isSearching={isSearching}
              highlightTexts={highlightTexts}
              isFolderListLoading={isFolderListLoading}
              isFolderListEmpty={isFolderListEmpty}
              onPageChanged={handlePageChange}
              onChangingFolderPermissions={() => setChangingFolderPermissions(true)}
              onSortChanged={onSortChanged}
              onRemoveDocument={onRemoveDocument}
              onRefresh={onRefresh}
              dropCompletedTrigger={dropCompletedCounter}
              isSearchingOcr={isSearchingOcr}
              exportSearchResultDocuments={handleExportSearchResultDocuments}
              isLoadingSearchResultFile={isLoadingSearchResultFile}
              mainFrameLeftSectionWidth={leftWidth}
              sharedToken={sharedToken}
              onClickShowAdvancedFilterMenu={handleClickShowAdvancedFilterMenu}
            />
            )}
            {changingFolderPermissions && (
            <FolderPermissionsSection
              mainFrameLeftSectionWidth={leftWidth}
              folder={selectedFolder}
              onCancel={() => setChangingFolderPermissions(false)}
              updateFolderList={() => setUpdateFolderList((prev) => prev + 1)}
            />
            )}
          </div>
        </div>
      </MainFrame>
      <CloseAlertModal open={closeAlertModal.open} text={closeAlertModal.text} onCancel={closeAlertModal.onCancel} />
      {isLoading ? <LoadingOverlay /> : null}
    </div>
  );
}

export default Component;
